import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import HealthIcon from '@mui/icons-material/HealthAndSafety';
import HistoryIcon from '@mui/icons-material/History';
import ReplyIcon from '@mui/icons-material/Reply';
import SendIcon from '@mui/icons-material/Send';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import StarterKit from '@tiptap/starter-kit';
import {
  RichTextEditor,
  type RichTextEditorRef,
  MenuControlsContainer,
  MenuButtonBold,
  MenuButtonItalic,
  MenuButtonUnderline,
  MenuButtonStrikethrough,
  MenuButtonCode,
  MenuButtonBulletedList,
  MenuButtonOrderedList,
  MenuButtonEditLink,
  LinkBubbleMenu,
  LinkBubbleMenuHandler,
  MenuDivider,
  MenuButtonUndo,
  MenuButtonRedo,
} from 'mui-tiptap';
import { v4 as uuidv4 } from 'uuid';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { updateEpic } from '../../store/slices/epicsSlice';
import type { HealthItem, HistoryChange, HistoryEntry, ReactionEmoji } from '../../store/slices/sharedTypes';
import {
  EngineeringSubtype,
  Story,
  StoryType,
  STORY_HEALTH_DEFINITIONS,
  addStandaloneStory,
  updateStandaloneStory,
  deleteStandaloneStory,
  addStoryComment,
  addStoryCommentReply,
  computeStoryHealthItems,
  getStoryHealthPercent,
  deleteStoryComment,
  deleteStoryCommentReply,
  toggleStoryCommentReaction,
  toggleStoryHealthAcknowledge,
  updateStoryComment,
  updateStoryCommentReply,
} from '../../store/slices/storiesSlice';
import {
  getHealthColor,
  getHealthTierLabel,
} from './Backlog';
import {
  PARENT_STATES, PARENT_STATE_COLORS, ParentState,
} from '../../store/slices/stateManagementSlice';
import ModalActionFooter from '../../components/ModalActionFooter/ModalActionFooter';
import NumberInput from '../../components/NumberInput/NumberInput';
import JsonViewer from '../../components/JsonViewer/JsonViewer';

/** Placeholder identity used until real auth is wired up. */
const CURRENT_USER_ID = 'current-user';
const CURRENT_USER_NAME = 'You';

const REACTION_EMOJIS: ReactionEmoji[] = ['👍', '🔥', '🎉'];

const ENGINEERING_SUBTYPES: EngineeringSubtype[] = [
  'Frontend',
  'Backend',
  'Quality',
  'DevOps',
  'Fullstack',
];

/** Format an ISO date string as a friendly relative or absolute label. */
const formatDate = (iso: string): string => {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

/** Derive user initials for Avatar fallback. */
const getInitials = (name: string): string =>
  name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

const StoryForm: React.FC = () => {
  const navigate = useNavigate();
  const { storyId } = useParams<{ storyId: string }>();
  const dispatch = useAppDispatch();

  const allEpics = useAppSelector((s) => s.epics.epics);
  const standaloneStories = useAppSelector((s) => s.stories.standaloneStories);
  const storyWeights = useAppSelector((s) => s.healthConfig.storyWeights);
  const storyStatuses = useAppSelector((s) => s.stateManagement.storyStatuses);
  const developerMode = useAppSelector((s) => s.developerMode.developerMode);

  /** Locate the story and its parent epic from the Redux store. */
  const { existingStory, parentEpic } = useMemo(() => {
    if (!storyId) return { existingStory: null, parentEpic: null };
    for (const epic of allEpics) {
      const story = (epic.stories ?? []).find((s) => s.id === storyId);
      if (story) return { existingStory: story, parentEpic: epic };
    }
    const standalone = standaloneStories.find((s) => s.id === storyId);
    if (standalone) return { existingStory: standalone, parentEpic: null };
    return { existingStory: null, parentEpic: null };
  }, [allEpics, standaloneStories, storyId]);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [epicId, setEpicId] = useState('');
  const [type, setType] = useState<StoryType>('Engineering');
  const [engineeringSubtype, setEngineeringSubtype] = useState<EngineeringSubtype>('Frontend');
  const [points, setPoints] = useState(0);
  const [acceptanceCriteria, setAcceptanceCriteria] = useState('');
  const [parentState, setParentState] = useState<ParentState | ''>('');
  const [status, setStatus] = useState('');

  // Right-panel tab state
  const [activeTab, setActiveTab] = useState(0);

  // JSON config viewer state
  const [jsonViewerOpen, setJsonViewerOpen] = useState(false);
  const [jsonCopied, setJsonCopied] = useState(false);

  // Comment editor state
  const [newCommentContent, setNewCommentContent] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentContent, setEditingCommentContent] = useState('');
  const [replyingToCommentId, setReplyingToCommentId] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [editingReplyContent, setEditingReplyContent] = useState('');

  const richTextEditorRef = useRef<RichTextEditorRef>(null);
  const commentEditorRef = useRef<RichTextEditorRef>(null);
  const editCommentEditorRef = useRef<RichTextEditorRef>(null);
  const extensions = useMemo(
    () => [StarterKit.configure({ link: { openOnClick: false } }), LinkBubbleMenuHandler],
    [],
  );

  // Track the last story ID we initialized local state from.
  // This ensures we only reset local form state when navigating to a *different*
  // story — not every time the same story changes in Redux (e.g. health acknowledgments).
  const initializedStoryId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (existingStory && initializedStoryId.current !== existingStory.id) {
      initializedStoryId.current = existingStory.id;
      setTitle(existingStory.title);
      setDescription(existingStory.description);
      setEpicId(parentEpic?.id ?? '');
      setType(existingStory.type);
      setEngineeringSubtype(existingStory.engineeringSubtype ?? 'Frontend');
      setPoints(existingStory.points);
      const criteriaContent = existingStory.acceptanceCriteria ?? '';
      setAcceptanceCriteria(criteriaContent);
      richTextEditorRef.current?.editor?.commands.setContent(criteriaContent);
      setParentState(existingStory.parentState ?? '');
      setStatus(existingStory.status ?? '');
    } else if (!existingStory) {
      // Reset so that if existingStory reappears (same ID) we re-initialize.
      initializedStoryId.current = undefined;
    }
  }, [existingStory, parentEpic]);

  const selectedEpic = useMemo(
    () => allEpics.find((e) => e.id === epicId) ?? null,
    [allEpics, epicId],
  );

  /** Compute health items for this story as currently edited. Only shown when
   *  editing an existing story (not on the create form). */
  const healthItems = useMemo((): HealthItem[] => {
    if (!existingStory) return [];
    const draft: Story = {
      ...existingStory,
      title,
      description,
      points,
      acceptanceCriteria,
    };
    const items = computeStoryHealthItems(draft, storyWeights);
    if (!epicId) {
      const noEpicDef = STORY_HEALTH_DEFINITIONS.find((d) => d.id === 'no_epic');
      if (noEpicDef) {
        items.push({
          id: noEpicDef.id,
          reason: noEpicDef.reason,
          score: storyWeights?.['no_epic'] ?? noEpicDef.score,
          acknowledged: !!(existingStory.acknowledgedHealthItems?.['no_epic']),
        });
      }
    }
    return items;
  }, [existingStory, title, description, points, acceptanceCriteria, epicId, storyWeights]);

  /** Health percentage (0-100) matching the score shown in the Backlog view.
   *  Only rendered when `showHealth` is true (i.e. `existingStory !== null`),
   *  so the early-return value of 100 for new stories is never displayed. */
  const healthPercent = useMemo((): number => {
    if (!existingStory) return 100;
    const draft: Story = {
      ...existingStory,
      title,
      description,
      points,
      acceptanceCriteria,
    };
    // getStoryHealthPercent checks description/points/acceptanceCriteria only;
    // epicId is not part of the standard health calculation (NO_EPIC is a
    // StoryForm-only indicator shown in healthItems but not in the backlog score).
    return getStoryHealthPercent(draft, storyWeights);
  }, [existingStory, title, description, points, acceptanceCriteria, storyWeights]);

  /** Interpolated RGB colour matching the Backlog's health chip colour. */
  const healthColor = getHealthColor(healthPercent);
  /** Human-readable tier label (e.g. "Healthy", "Attention") matching the Backlog. */
  const healthLabel = getHealthTierLabel(healthPercent);

  const comments = existingStory?.comments ?? [];
  const commentsCount = comments.length;

  const handleToggleHealthItem = (itemId: string) => {
    if (!existingStory) return;
    dispatch(
      toggleStoryHealthAcknowledge({
        epicId: parentEpic?.id ?? '',
        storyId: existingStory.id,
        healthItemId: itemId,
      }),
    );
  };

  /** Compute a list of field changes to record in the history entry on save. */
  const computeHistoryChanges = (): HistoryChange[] => {
    if (!existingStory) return [];
    const changes: HistoryChange[] = [];
    if (existingStory.title !== title.trim()) {
      changes.push({ field: 'Title', from: existingStory.title, to: title.trim() });
    }
    if ((existingStory.description ?? '') !== description) {
      changes.push({ field: 'Description', from: existingStory.description || '(empty)', to: description || '(empty)' });
    }
    if (existingStory.points !== points) {
      changes.push({ field: 'Story Points', from: String(existingStory.points), to: String(points) });
    }
    if (existingStory.type !== type) {
      changes.push({ field: 'Story Type', from: existingStory.type, to: type });
    }
    if ((existingStory.engineeringSubtype ?? 'Frontend') !== engineeringSubtype) {
      changes.push({ field: 'Engineering Type', from: existingStory.engineeringSubtype ?? '(none)', to: engineeringSubtype });
    }
    if ((parentEpic?.id ?? '') !== epicId) {
      const oldTitle = parentEpic?.title ?? '(none)';
      const newTitle = allEpics.find((e) => e.id === epicId)?.title ?? '(none)';
      changes.push({ field: 'Epic', from: oldTitle, to: newTitle });
    }
    if ((existingStory.parentState ?? '') !== parentState) {
      changes.push({ field: 'State', from: existingStory.parentState ?? '(none)', to: parentState || '(none)' });
    }
    if ((existingStory.status ?? '') !== status) {
      changes.push({ field: 'Status', from: existingStory.status ?? '(none)', to: status || '(none)' });
    }
    return changes;
  };

  const handleSave = () => {
    if (!title.trim()) return;
    const targetEpic = epicId ? allEpics.find((e) => e.id === epicId) : null;
    if (epicId && !targetEpic) return;

    const changes = computeHistoryChanges();
    const newHistoryEntries: HistoryEntry[] = changes.length > 0 && existingStory
      ? [{ id: uuidv4(), timestamp: new Date().toISOString(), changedBy: CURRENT_USER_NAME, changes }]
      : [];

    const storyData: Story = {
      id: existingStory?.id ?? uuidv4(),
      title: title.trim(),
      description,
      type,
      ...(type === 'Engineering' && { engineeringSubtype }),
      points,
      acceptanceCriteria,
      parentState: parentState || undefined,
      status: status || undefined,
      acknowledgedHealthItems: existingStory?.acknowledgedHealthItems,
      comments: existingStory?.comments ?? [],
      history: [...newHistoryEntries, ...(existingStory?.history ?? [])],
    };

    if (existingStory) {
      if (!epicId) {
        // Saving as standalone (no epic)
        if (parentEpic) {
          // Move out of epic into standalone
          dispatch(
            updateEpic({
              ...parentEpic,
              stories: (parentEpic.stories ?? []).filter((s) => s.id !== existingStory.id),
            }),
          );
          dispatch(addStandaloneStory(storyData));
        } else {
          // Update standalone in place
          dispatch(updateStandaloneStory(storyData));
        }
      } else if (parentEpic?.id === epicId) {
        // Update in place inside the same epic
        dispatch(
          updateEpic({
            ...parentEpic,
            stories: (parentEpic.stories ?? []).map((s) =>
              s.id === existingStory.id ? storyData : s,
            ),
          }),
        );
      } else {
        // Move to a different epic (or from standalone to epic)
        if (parentEpic) {
          dispatch(
            updateEpic({
              ...parentEpic,
              stories: (parentEpic.stories ?? []).filter((s) => s.id !== existingStory.id),
            }),
          );
        } else {
          // Was standalone, now moving to an epic
          dispatch(deleteStandaloneStory(existingStory.id));
        }
        if (targetEpic) {
          dispatch(
            updateEpic({
              ...targetEpic,
              stories: [...(targetEpic.stories ?? []), storyData],
            }),
          );
        }
      }
    } else if (targetEpic) {
      // Create new story inside an epic
      dispatch(
        updateEpic({
          ...targetEpic,
          stories: [...(targetEpic.stories ?? []), storyData],
        }),
      );
    } else {
      // Create new standalone story
      dispatch(addStandaloneStory(storyData));
    }

    navigate('/backlog');
  };

  const handleDelete = () => {
    if (!existingStory) return;
    if (parentEpic) {
      dispatch(
        updateEpic({
          ...parentEpic,
          stories: (parentEpic.stories ?? []).filter((s) => s.id !== existingStory.id),
        }),
      );
    } else {
      dispatch(deleteStandaloneStory(existingStory.id));
    }
    navigate('/backlog');
  };

  // ── Comment handlers ────────────────────────────────────────────────────

  const handlePostComment = () => {
    if (!existingStory || !newCommentContent.trim()) return;
    dispatch(addStoryComment({
      epicId: parentEpic?.id ?? '',
      storyId: existingStory.id,
      comment: {
        authorId: CURRENT_USER_ID,
        authorName: CURRENT_USER_NAME,
        content: newCommentContent,
      },
    }));
    setNewCommentContent('');
    commentEditorRef.current?.editor?.commands.clearContent();
  };

  const handleSaveEditComment = (commentId: string) => {
    if (!existingStory || !editingCommentContent.trim()) return;
    dispatch(updateStoryComment({
      epicId: parentEpic?.id ?? '',
      storyId: existingStory.id,
      commentId,
      content: editingCommentContent,
    }));
    setEditingCommentId(null);
    setEditingCommentContent('');
  };

  const handleDeleteComment = (commentId: string) => {
    if (!existingStory) return;
    dispatch(deleteStoryComment({ epicId: parentEpic?.id ?? '', storyId: existingStory.id, commentId }));
  };

  const handleToggleReaction = (commentId: string, emoji: ReactionEmoji) => {
    if (!existingStory) return;
    dispatch(toggleStoryCommentReaction({
      epicId: parentEpic?.id ?? '',
      storyId: existingStory.id,
      commentId,
      emoji,
      userId: CURRENT_USER_ID,
    }));
  };

  const handlePostReply = (commentId: string) => {
    if (!existingStory || !replyContent.trim()) return;
    dispatch(addStoryCommentReply({
      epicId: parentEpic?.id ?? '',
      storyId: existingStory.id,
      commentId,
      reply: {
        authorId: CURRENT_USER_ID,
        authorName: CURRENT_USER_NAME,
        content: replyContent.trim(),
      },
    }));
    setReplyContent('');
    setReplyingToCommentId(null);
  };

  const handleSaveEditReply = (commentId: string, replyId: string) => {
    if (!existingStory || !editingReplyContent.trim()) return;
    dispatch(updateStoryCommentReply({
      epicId: parentEpic?.id ?? '',
      storyId: existingStory.id,
      commentId,
      replyId,
      content: editingReplyContent.trim(),
    }));
    setEditingReplyId(null);
    setEditingReplyContent('');
  };

  const handleDeleteReply = (commentId: string, replyId: string) => {
    if (!existingStory) return;
    dispatch(deleteStoryCommentReply({
      epicId: parentEpic?.id ?? '',
      storyId: existingStory.id,
      commentId,
      replyId,
    }));
  };

  const showHealth = existingStory !== null;

  /**
   * True when this is an existing closed story and the local state selection is
   * still 'Closed'. All fields except the State selector are disabled in this
   * case — the user must change the state away from 'Closed' to unlock editing.
   */
  const isFormLocked = !!(existingStory && parentState === 'Closed');

  return (
    <>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
            gap: 3,
            alignItems: 'start',
          }}
        >
          {/* ── Left Paper: Main Form ── */}
          <Paper elevation={2} sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0 }}>
              <ArticleIcon fontSize="large" sx={{ color: '#2196F3' }} />
              <Typography variant="h5" fontWeight={600}>
                {existingStory ? title : 'New Story'}
              </Typography>
              {showHealth && (
                <Tooltip title={`Click to view health details · ${healthLabel}`} arrow>
                  <Chip
                    icon={<HealthIcon />}
                    label={`Health: ${healthPercent}%`}
                    size="medium"
                    onClick={() => setActiveTab(2)}
                    sx={{ ml: 'auto', cursor: 'pointer', fontWeight: 600, bgcolor: healthColor, color: '#fff', '& .MuiChip-icon': { color: '#fff' } }}
                  />
                </Tooltip>
              )}
              {!showHealth && developerMode && <Box sx={{ ml: 'auto' }} />}
              {developerMode && existingStory && (
                <Tooltip title="View configuration (JSON)" arrow>
                  <IconButton
                    size="small"
                    onClick={() => { setJsonCopied(false); setJsonViewerOpen(true); }}
                    sx={{ color: 'text.secondary', '&:hover': { color: 'warning.main' } }}
                  >
                    <CodeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Divider sx={{ mt: 1, mb: 3 }} />

            {isFormLocked && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'action.disabledBackground',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 2,
                  py: 1.5,
                  mb: 2,
                }}
              >
                <LockOutlinedIcon sx={{ color: 'text.disabled', fontSize: 18, flexShrink: 0 }} />
                <Typography variant="body2" color="text.disabled">
                  This story is closed and cannot be edited. Change the state in the panel on the right to reopen it.
                </Typography>
              </Box>
            )}

            <Stack spacing={3}>
              {/* Title */}
              <TextField
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                required
                disabled={isFormLocked}
              />

              {/* Description */}
              <TextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
                disabled={isFormLocked}
              />

              {/* Acceptance Criteria */}
              <FormControl fullWidth sx={{ pointerEvents: isFormLocked ? 'none' : 'auto', opacity: isFormLocked ? 0.5 : 1 }}>

                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>Acceptance Criteria</Typography>

                <Box
                  sx={{
                    resize: 'vertical',
                    overflow: 'hidden',
                    minHeight: 150,
                    height: 300,
                  }}
                >
                  <RichTextEditor
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      '& .MuiTiptap-RichTextField-content': {
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                      },
                    }}
                    ref={richTextEditorRef}
                    extensions={extensions}
                    content={acceptanceCriteria}
                    onUpdate={({ editor }) => setAcceptanceCriteria(editor.getHTML())}
                    renderControls={() => (
                      <MenuControlsContainer>
                        <MenuButtonBold />
                        <MenuButtonItalic />
                        <MenuButtonUnderline />
                        <MenuButtonStrikethrough />
                        <MenuButtonCode />
                        <MenuDivider />
                        <MenuButtonBulletedList />
                        <MenuButtonOrderedList />
                        <MenuDivider />
                        <MenuButtonEditLink />
                        <MenuDivider />
                        <MenuButtonUndo />
                        <MenuButtonRedo />
                      </MenuControlsContainer>
                    )}
                    RichTextFieldProps={{ variant: 'outlined' }}
                  >
                    {() => <LinkBubbleMenu />}
                  </RichTextEditor>
                </Box>
              </FormControl>
            </Stack>

            <Box sx={{ mt: 4 }}>
              <ModalActionFooter
                onSave={handleSave}
                saveLabel={existingStory ? 'Save' : 'Create'}
                saveDisabled={!title.trim() || isFormLocked}
                onCancel={() => navigate('/backlog')}
                onDelete={existingStory ? handleDelete : undefined}
                deleteLabel="Delete"
                deleteConfirmMessage={existingStory ? `Delete "${title}"?` : undefined}
              />
            </Box>
          </Paper>

          {/* ── Right Paper: Controls ── */}
          <Paper elevation={2} sx={{ overflow: 'hidden', position: { md: 'sticky' }, top: { md: 80 }, height: '100%' }}>
            <Box sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Typography variant="subtitle1" color="text.secondary">Story Controls</Typography>

                {/* Story Type */}

                {/* Epic assignment */}
                <Autocomplete
                  options={allEpics}
                  getOptionLabel={(option) => option.title}
                  value={selectedEpic}
                  onChange={(_e, val) => setEpicId(val ? val.id : '')}
                  renderInput={(params) => (
                    <TextField {...params} label="Epic" size="small" />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  size="small"
                  disabled={isFormLocked}
                />

                {type === 'Engineering' && (
                  <TextField
                    select
                    label="Engineering Type"
                    value={engineeringSubtype}
                    onChange={(e) => setEngineeringSubtype(e.target.value as EngineeringSubtype)}
                    fullWidth
                    size="small"
                    disabled={isFormLocked}
                  >
                    {ENGINEERING_SUBTYPES.map((sub) => (
                      <MenuItem key={sub} value={sub}>{sub}</MenuItem>
                    ))}
                  </TextField>
                )}

                {/* State */}
                <TextField
                  select
                  label="State"
                  value={parentState}
                  onChange={(e) => {
                    setParentState(e.target.value as ParentState | '');
                    setStatus('');
                  }}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {PARENT_STATES.map((ps) => (
                    <MenuItem key={ps} value={ps}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PARENT_STATE_COLORS[ps], flexShrink: 0 }} />
                        {ps}
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>

                {/* Status */}
                {parentState && storyStatuses[parentState].length > 0 && (
                  <TextField
                    select
                    label="Status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {storyStatuses[parentState].map((st) => (
                      <MenuItem key={st.id} value={st.title}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: st.color, flexShrink: 0 }} />
                          {st.title}
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {/* Story Points */}
                <Box sx={{ pointerEvents: isFormLocked ? 'none' : 'auto', opacity: isFormLocked ? 0.5 : 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>Story Points</Typography>
                  <NumberInput value={points} onChange={setPoints} min={0} />
                </Box>
              </Stack>
            </Box>
          </Paper>
        </Box>

        {/* ── Bottom Paper: Tabbed Panel (History, Health, Comments) ── */}
        <Paper elevation={2} sx={{ mt: 3, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              icon={
                <Badge badgeContent={commentsCount > 0 ? commentsCount : undefined} color="primary" max={99}>
                  <ChatBubbleOutlineIcon fontSize="small" />
                </Badge>
              }
              aria-label="Comments"
              title="Comments"
              sx={{ minWidth: 0, py: 1.5 }}
            />
            <Tab
              icon={<HistoryIcon fontSize="small" />}
              aria-label="History"
              title="Change History"
              sx={{ minWidth: 0, py: 1.5 }}
            />
            <Tab
              icon={
                <Tooltip title={showHealth ? `Health: ${healthPercent}% · ${healthLabel}` : 'Health'}>
                  <HealthIcon
                    fontSize="small"
                    sx={{ color: showHealth ? healthColor : 'inherit' }}
                  />
                </Tooltip>
              }
              aria-label="Health"
              title="Health"
              sx={{ minWidth: 0, py: 1.5 }}
            />
          </Tabs>

          {/* ── Tab 1: History ── */}
          {activeTab === 1 && (
            <Box sx={{ p: 2, maxHeight: 520, overflowY: 'auto' }}>
              <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1.5 }}>
                Change History
              </Typography>
              {(!existingStory?.history || existingStory.history.length === 0) ? (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <HistoryIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                  <Typography variant="body2">No history yet. Changes are recorded each time you save.</Typography>
                </Box>
              ) : (
                <Stack spacing={1.5} divider={<Divider />}>
                  {existingStory.history.map((entry) => (
                    <Box key={entry.id} sx={{ display: 'flex', gap: 1.5, mb: 1, backgroundColor: 'background.paper', p: 2, borderRadius: 1, borderColor: 'divider', borderWidth: 1, borderStyle: 'solid' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HistoryIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(entry.timestamp)} · {entry.changedBy}
                        </Typography>
                      </Box>
                      <Stack spacing={0.5} sx={{ pl: 3 }}>
                        {entry.changes.map((change, idx) => (
                          <Box key={idx} sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Typography variant="caption" fontWeight={600}>{change.field}:</Typography>
                            <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'error.main' }}>{change.from}</Typography>
                            <Typography variant="caption" color="text.secondary">→</Typography>
                            <Typography variant="caption" sx={{ color: 'success.dark' }}>{change.to}</Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          )
          }

          {/* ── Tab 2: Health ── */}
          {
            activeTab === 2 && (
              <Box sx={{ p: 2, maxHeight: 520, overflowY: 'auto' }}>
                {!showHealth ? (
                  <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    <HealthIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                    <Typography variant="body2">
                      Save the story first to see health information.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <WarningAmberIcon
                        fontSize="small"
                        sx={{ color: healthColor }}
                      />
                      <Typography variant="subtitle1" color='text.secondary'>Story Health</Typography>
                      <Chip
                        label={`${healthPercent}%`}
                        size="small"
                        sx={{ ml: 'auto', fontWeight: 600, bgcolor: healthColor, color: '#fff' }}
                      />
                    </Box>
                    {healthItems.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 32, opacity: 0.5, mb: 0.5 }} />
                        <Typography variant="body2">No health issues detected.</Typography>
                      </Box>
                    ) : (
                      <Stack spacing={1}>
                        {healthItems.map((item) => (
                          <Box
                            key={item.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              p: 1,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: item.acknowledged ? 'success.main' : 'warning.main',
                              bgcolor: item.acknowledged ? 'success.light' : 'warning.light',
                              opacity: item.acknowledged ? 0.75 : 1,
                            }}
                          >
                            <Tooltip
                              title={item.acknowledged ? 'Click to unacknowledge this health issue' : 'Click to acknowledge this health issue'}
                              arrow
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleToggleHealthItem(item.id)}
                                sx={{ color: item.acknowledged ? 'success.dark' : 'warning.dark' }}
                                aria-label={item.acknowledged ? `Unacknowledge: ${item.reason}` : `Acknowledge: ${item.reason}`}
                              >
                                {item.acknowledged ? (
                                  <CheckCircleOutlineIcon fontSize="small" />
                                ) : (
                                  <WarningAmberIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Typography
                              variant="body2"
                              sx={{ flexGrow: 1, color: item.acknowledged ? 'success.dark' : 'warning.dark' }}
                            >
                              {item.reason}
                            </Typography>
                            <Chip
                              label={`+${item.score}`}
                              size="small"
                              color={item.acknowledged ? 'success' : 'warning'}
                              variant={item.acknowledged ? 'outlined' : 'filled'}
                              sx={{ fontSize: '0.65rem', fontWeight: 600 }}
                            />
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </>
                )}
              </Box>
            )
          }

          {/* ── Tab 0: Comments ── */}
          {
            activeTab === 0 && (
              <Box sx={{ p: 2, overflowY: 'auto' }}>
                {existingStory && parentEpic ? (
                  <>
                    <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
                      Add a comment
                    </Typography>
                    <Box sx={{ mb: 1 }}>
                      <RichTextEditor
                        sx={{
                          '& .MuiTiptap-RichTextField-content': { minHeight: 80 },
                        }}
                        ref={commentEditorRef}
                        extensions={extensions}
                        content={newCommentContent}
                        onUpdate={({ editor }) => setNewCommentContent(editor.getHTML())}
                        renderControls={() => (
                          <MenuControlsContainer>
                            <MenuButtonBold />
                            <MenuButtonItalic />
                            <MenuButtonUnderline />
                            <MenuDivider />
                            <MenuButtonBulletedList />
                            <MenuButtonOrderedList />
                            <MenuDivider />
                            <MenuButtonUndo />
                            <MenuButtonRedo />
                          </MenuControlsContainer>
                        )}
                        RichTextFieldProps={{ variant: 'outlined' }}
                      >
                        {() => <LinkBubbleMenu />}
                      </RichTextEditor>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<SendIcon fontSize="small" />}
                        onClick={handlePostComment}
                        disabled={!newCommentContent.trim() || newCommentContent === '<p></p>'}
                      >
                        Post
                      </Button>
                    </Box>

                    {/* Comment Stack */}
                    {comments.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                        <ChatBubbleOutlineIcon sx={{ fontSize: 36, opacity: 0.3, mb: 1 }} />
                        <Typography variant="body2">No comments yet. Be the first to comment!</Typography>
                      </Box>
                    ) : (
                      <Stack spacing={2}>
                        {comments.map((comment) => (
                          <Box key={comment.id}>
                            <Box sx={{ display: 'flex', gap: 1.5, mb: 1, backgroundColor: 'background.paper', p: 1, borderRadius: 1, borderColor: 'divider', borderWidth: 1, borderStyle: 'solid' }}>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.75rem' }}>
                                {getInitials(comment.authorName)}
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography variant="body2" fontWeight={600}>{comment.authorName}</Typography>
                                  <Typography variant="caption" color="text.secondary">{formatDate(comment.createdAt)}</Typography>
                                  {comment.updatedAt && (
                                    <Typography variant="caption" color="text.secondary">(edited)</Typography>
                                  )}
                                </Box>

                                {/* Comment body or inline editor */}
                                {editingCommentId === comment.id ? (
                                  <Box sx={{ mt: 1 }}>
                                    <RichTextEditor
                                      ref={editCommentEditorRef}
                                      extensions={extensions}
                                      content={editingCommentContent}
                                      onUpdate={({ editor }) => setEditingCommentContent(editor.getHTML())}
                                      renderControls={() => (
                                        <MenuControlsContainer>
                                          <MenuButtonBold />
                                          <MenuButtonItalic />
                                          <MenuButtonUnderline />
                                          <MenuDivider />
                                          <MenuButtonBulletedList />
                                          <MenuButtonOrderedList />
                                        </MenuControlsContainer>
                                      )}
                                      RichTextFieldProps={{ variant: 'outlined' }}
                                    >
                                      {() => <></>}
                                    </RichTextEditor>
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
                                      <Button size="small" onClick={() => setEditingCommentId(null)}>Cancel</Button>
                                      <Button size="small" variant="contained" onClick={() => handleSaveEditComment(comment.id)}>Save</Button>
                                    </Box>
                                  </Box>
                                ) : (
                                  <Box
                                    sx={{ mt: 0.5, fontSize: '0.875rem', '& p': { margin: 0 }, '& ul, & ol': { pl: 2, my: 0.5 } }}
                                    dangerouslySetInnerHTML={{ __html: comment.content }}
                                  />
                                )}

                                {/* Reactions */}
                                <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                  {REACTION_EMOJIS.map((emoji) => {
                                    const reaction = comment.reactions.find((r) => r.emoji === emoji);
                                    const count = reaction?.userIds.length ?? 0;
                                    const active = reaction?.userIds.includes(CURRENT_USER_ID) ?? false;
                                    return (
                                      <Chip
                                        key={emoji}
                                        label={count > 0 ? `${emoji} ${count}` : emoji}
                                        size="small"
                                        variant={active ? 'filled' : 'outlined'}
                                        color={active ? 'primary' : 'default'}
                                        onClick={() => handleToggleReaction(comment.id, emoji)}
                                        sx={{ cursor: 'pointer', fontSize: '0.85rem', height: 24 }}
                                      />
                                    );
                                  })}
                                  <Tooltip title="Reply to this comment">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setReplyingToCommentId(replyingToCommentId === comment.id ? null : comment.id);
                                        setReplyContent('');
                                      }}
                                      sx={{ ml: 0.5 }}
                                      aria-label="Reply"
                                    >
                                      <ReplyIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {comment.authorId === CURRENT_USER_ID && (
                                    <>
                                      <Tooltip title="Edit comment">
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            setEditingCommentId(comment.id);
                                            setEditingCommentContent(comment.content);
                                            setTimeout(() => {
                                              editCommentEditorRef.current?.editor?.commands.setContent(comment.content);
                                            }, 0);
                                          }}
                                          aria-label="Edit comment"
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Delete comment">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleDeleteComment(comment.id)}
                                          aria-label="Delete comment"
                                          sx={{ color: 'error.main' }}
                                        >
                                          <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  )}
                                </Box>

                                {/* Replies */}
                                {comment.replies.length > 0 && (
                                  <Stack spacing={1} sx={{ mt: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                                    {comment.replies.map((reply) => (
                                      <Box key={reply.id} sx={{ display: 'flex', gap: 1 }}>
                                        <Avatar sx={{ width: 24, height: 24, bgcolor: 'secondary.main', fontSize: '0.65rem', flexShrink: 0 }}>
                                          {getInitials(reply.authorName)}
                                        </Avatar>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Typography variant="caption" fontWeight={600}>{reply.authorName}</Typography>
                                            <Typography variant="caption" color="text.secondary">{formatDate(reply.createdAt)}</Typography>
                                            {reply.updatedAt && (
                                              <Typography variant="caption" color="text.secondary">(edited)</Typography>
                                            )}
                                          </Box>
                                          {editingReplyId === reply.id ? (
                                            <Box>
                                              <TextField
                                                size="small"
                                                fullWidth
                                                multiline
                                                maxRows={4}
                                                value={editingReplyContent}
                                                onChange={(e) => setEditingReplyContent(e.target.value)}
                                                sx={{ mt: 0.5 }}
                                              />
                                              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, justifyContent: 'flex-end' }}>
                                                <Button size="small" onClick={() => setEditingReplyId(null)}>Cancel</Button>
                                                <Button size="small" variant="contained" onClick={() => handleSaveEditReply(comment.id, reply.id)}>Save</Button>
                                              </Box>
                                            </Box>
                                          ) : (
                                            <Typography variant="body2" sx={{ mt: 0.25 }}>{reply.content}</Typography>
                                          )}
                                          {reply.authorId === CURRENT_USER_ID && editingReplyId !== reply.id && (
                                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                              <IconButton size="small" onClick={() => { setEditingReplyId(reply.id); setEditingReplyContent(reply.content); }} aria-label="Edit reply">
                                                <EditIcon sx={{ fontSize: 14 }} />
                                              </IconButton>
                                              <IconButton size="small" onClick={() => handleDeleteReply(comment.id, reply.id)} sx={{ color: 'error.main' }} aria-label="Delete reply">
                                                <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                                              </IconButton>
                                            </Box>
                                          )}
                                        </Box>
                                      </Box>
                                    ))}
                                  </Stack>
                                )}

                                {/* Reply input */}
                                {replyingToCommentId === comment.id && (
                                  <Box sx={{ mt: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'primary.main' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                      Replying to <strong>{comment.authorName}</strong>
                                    </Typography>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      multiline
                                      maxRows={4}
                                      placeholder="Write a reply…"
                                      value={replyContent}
                                      onChange={(e) => setReplyContent(e.target.value)}
                                    />
                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, justifyContent: 'flex-end' }}>
                                      <Button size="small" onClick={() => setReplyingToCommentId(null)}>Cancel</Button>
                                      <Button size="small" variant="contained" disabled={!replyContent.trim()} onClick={() => handlePostReply(comment.id)}>Reply</Button>
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    <ChatBubbleOutlineIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                    <Typography variant="body2">Save the story first to add comments.</Typography>
                  </Box>
                )}
              </Box>
            )
          }
        </Paper >
      </Box >

      {/* JSON Config Viewer Modal */}
      {developerMode && existingStory && (
        <Dialog open={jsonViewerOpen} onClose={() => setJsonViewerOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CodeIcon sx={{ color: 'warning.main' }} />
                <Typography variant="h6">Story Configuration</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title={jsonCopied ? 'Copied!' : 'Copy JSON'} arrow>
                  <IconButton
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(JSON.stringify(existingStory, null, 2));
                      setJsonCopied(true);
                      setTimeout(() => setJsonCopied(false), 2000);
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close" arrow>
                  <IconButton size="small" onClick={() => setJsonViewerOpen(false)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <JsonViewer jsonStr={JSON.stringify(existingStory, null, 2)} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default StoryForm;
